import { DependencyList, useEffect, useState } from "react";
import { CommandManager } from "./command-manager";
import { Command } from "./types";

/**
 * React hook to manage a CommandManager instance tied to the component lifecycle.
 * Registers and unregisters commands based on the provided `commands` array,
 * re-running the synchronization logic when dependencies in `deps` change.
 *
 * @param commands The array of Command objects to be managed. Defaults to [].
 * @param deps The dependency list for the useEffect hook. The command synchronization
 * logic (`updateCommands`) will re-run when any value in this list changes.
 * **Important:** You MUST include all external values that the `commands` array
 * generation depends on. The hook intentionally omits `commands` itself from the
 * effect's dependency list to give you explicit control via `deps`.
 * @returns The CommandManager instance (primarily for potential advanced use or debugging).
 */
export function useCommandManager(
  commands: Command[] = [],
  deps: DependencyList = [],
): CommandManager {
  const [manager] = useState(() => new CommandManager());

  useEffect(() => {
    manager.updateCommands(commands);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager, ...deps]);

  useEffect(() => {
    return () => {
      manager.cleanup();
    };
  }, [manager]);

  return manager;
}
